import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ShareBar from '../components/ShareBar';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import Disclaimer from '../components/Disclaimer';
import { ALL_STATES } from '../data/savingsByState';

const REGIONS = [
  { label: 'Northeast', states: ['connecticut','maine','massachusetts','new-hampshire','new-jersey','new-york','pennsylvania','rhode-island','vermont'] },
  { label: 'South', states: ['alabama','arkansas','delaware','florida','georgia','kentucky','louisiana','maryland','mississippi','north-carolina','south-carolina','tennessee','virginia','west-virginia'] },
  { label: 'Midwest', states: ['illinois','indiana','iowa','kansas','michigan','minnesota','missouri','nebraska','north-dakota','ohio','south-dakota','wisconsin'] },
  { label: 'West', states: ['alaska','arizona','california','colorado','hawaii','idaho','montana','nevada','new-mexico','oregon','utah','washington','wyoming'] },
  { label: 'Texas', states: ['texas'] },
];

function fmt(n) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

function getRetirementColor(rank) {
  if (rank <= 10) return '#22c55e';
  if (rank <= 25) return '#38bdf8';
  if (rank <= 40) return '#f59e0b';
  return '#f87171';
}



export default function SavingsByStateHub() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const [filter, setFilter] = useState(params.get('region') || 'All');
  const [search, setSearch] = useState('');

  // Sync filter to URL
  const handleFilter = (r) => {
    const newParams = new URLSearchParams(location.search);
    if (r === 'All') { newParams.delete('region'); } else { newParams.set('region', r); }
    navigate({ search: newParams.toString() }, { replace: true });
    setFilter(r);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sync from URL on mount/back nav
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setFilter(p.get('region') || 'All');
  }, [location.search]);

  const stateList = ALL_STATES
    .filter(s => filter === 'All' || REGIONS.find(r => r.label === filter)?.states.includes(s.slug))
    .filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.abbr.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.rank - b.rank);

  // Top 5 and bottom 5
  const allSorted = [...ALL_STATES].sort((a, b) => a.rank - b.rank);
  const top5 = allSorted.slice(0, 5);
  const bottom5 = allSorted.slice(-5).reverse();

  return (
    <div style={s.page}>
      <SEO
        title="Average Savings by State — All 50 States | KnowFi"
        description="Compare average retirement savings, savings rates, and cost of living across all 50 US states. See which states save the most, which have the highest debt, and where your state ranks."
        path="/savings-by-state"
        keywords="average savings by state, retirement savings by state, which state saves the most money, savings rate by state, cost of living by state, personal finance by state, how much does the average person save by state, average retirement savings by state, best states to save money, worst states for savings, household savings by state"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Average Savings by State — All 50 States',
          description: 'Compare average retirement savings, savings rates, and cost of living across all 50 US states.',
          url: 'https://knowfi.io/savings-by-state',
          publisher: { '@type': 'Organization', name: 'KnowFi', url: 'https://knowfi.io' },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://knowfi.io' },
              { '@type': 'ListItem', position: 2, name: 'Data & Tools', item: 'https://knowfi.io/blog?tab=data' },
              { '@type': 'ListItem', position: 3, name: 'Average Savings by State', item: 'https://knowfi.io/savings-by-state' },
            ],
          },
        })}</script>
      </Helmet>

      <div style={s.glow} />
      <div style={s.inner}>

        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <Link to="/" style={s.breadLink}>Home</Link>
          <span style={s.breadSep}>›</span>
          <Link to="/blog?tab=data" style={s.breadLink}>Data & Tools</Link>
          <span style={s.breadSep}>›</span>
          <span style={s.breadCurrent}>Savings by State</span>
        </div>

        {/* Header */}
        <div style={s.header}>
          <div style={s.badge}>📍 State Data</div>
          <h1 style={s.h1}>Average Savings by State</h1>
          <p style={s.subtitle}>
            How much does the average person save in your state? Compare retirement savings,
            savings rates, and cost of living across all 50 states.
          </p>
        </div>

        {/* Share */}
        <div style={{ marginBottom: '24px' }}>
          <ShareBar
            text={filter === 'All' ? 'Average savings by state — all 50 states ranked | KnowFi' : `Average savings in the ${filter} — see how your state ranks | KnowFi`}
            url={`https://knowfi.io${filter === 'All' ? '/savings-by-state' : `/savings-by-state?region=${encodeURIComponent(filter)}`}`}
          />
        </div>

        {/* Top/Bottom highlight */}
        <div style={s.highlightRow}>
          <div style={s.highlightBox}>
            <div style={s.hlTitle}>🏆 Most Saved for Retirement</div>
            {top5.map(s2 => (
              <Link key={s2.slug} to={`/savings-by-state/${s2.slug}`} style={s.hlRow}>
                <span style={s.hlName}>{s2.name}</span>
                <span style={{ ...s.hlVal, color: '#22c55e' }}>{fmt(s2.avgRetirementSavings)} total saved</span>
              </Link>
            ))}
          </div>
          <div style={s.highlightBox}>
            <div style={s.hlTitle}>📉 Least Saved for Retirement</div>
            {bottom5.map(s2 => (
              <Link key={s2.slug} to={`/savings-by-state/${s2.slug}`} style={s.hlRow}>
                <span style={s.hlName}>{s2.name}</span>
                <span style={{ ...s.hlVal, color: '#f87171' }}>{fmt(s2.avgRetirementSavings)} total saved</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Filter bar */}
        <div style={s.filterBar}>
          {['All', 'Northeast', 'South', 'Midwest', 'West', 'Texas'].map(r => (
            <button key={r} onClick={() => handleFilter(r)} style={{ ...s.filterBtn, ...(filter === r ? s.filterBtnActive : {}) }}>
              {r}
            </button>
          ))}
          <input
            style={s.searchInput}
            placeholder="Search state..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* State grid */}
        <div style={s.grid}>
          {stateList.map(st => (
            <Link key={st.slug} to={`/savings-by-state/${st.slug}`} style={s.card}>
              <div style={s.cardTop}>
                <div style={{ ...s.abbr, color: getRetirementColor(st.rank) }}>{st.abbr}</div>
                <div style={s.stateName}>{st.name}</div>
                <div style={{ ...s.rankBadge, color: getRetirementColor(st.rank) }}>#{st.rank}</div>
              </div>
              <div style={s.stat}>
                <span style={s.statLabel}>Retirement saved</span>
                <span style={{ ...s.statVal, color: getRetirementColor(st.rank) }}>{fmt(st.avgRetirementSavings)}</span>
              </div>
              <div style={s.stat}>
                <span style={s.statLabel}>Saves per year</span>
                <span style={s.statValGray}>{st.avgSavingsRate}% of income</span>
              </div>
              <div style={s.stat}>
                <span style={s.statLabel}>Cost of living</span>
                <span style={s.statValGray}>{st.costOfLivingIndex} (US avg = 100)</span>
              </div>
              <div style={{ marginTop: '10px' }} onClick={e => e.preventDefault()}>
                <ShareBar
                  text={`${st.name} retirement savings: avg ${fmt(st.avgRetirementSavings)} saved — ranked #${st.rank} in the US | KnowFi`}
                  url={`https://knowfi.io/savings-by-state/${st.slug}`}
                  compact
                />
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div style={s.ctaSection}>
          <div style={s.ctaTitle}>📐 See how you compare</div>
          <div style={s.ctaRow}>
            <Link to="/calculators" style={s.ctaBtn}>🔢 Retirement Calculator</Link>
            <Link to="/savings-by-age" style={s.ctaBtnSecondary}>📊 Savings by Age</Link>
          </div>
        </div>

        <Disclaimer />
      </div>
    </div>
  );
}

const s = {
  page:     { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '40px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:     { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '400px', background: 'radial-gradient(ellipse at center, rgba(167,139,250,0.06) 0%, transparent 65%)', pointerEvents: 'none' },
  inner:    { maxWidth: '960px', margin: '0 auto', position: 'relative', zIndex: 1 },

  breadcrumb:   { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px' },
  breadLink:    { color: '#475569', textDecoration: 'none', fontSize: '13px' },
  breadSep:     { color: '#334155', fontSize: '13px' },
  breadCurrent: { color: '#64748b', fontSize: '13px' },

  header:   { marginBottom: '32px' },
  badge:    { display: 'inline-block', background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.25)', color: '#a78bfa', fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' },
  h1:       { fontSize: 'clamp(24px, 4vw, 40px)', fontWeight: '900', margin: '0 0 14px', letterSpacing: '-0.5px', lineHeight: 1.2, color: '#f1f5f9' },
  subtitle: { fontSize: '17px', color: '#64748b', margin: 0, lineHeight: 1.65, maxWidth: '640px' },

  highlightRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '32px' },
  highlightBox: { background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', padding: '18px' },
  hlTitle:      { fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },
  hlRow:        { display: 'flex', flexDirection: 'column', padding: '6px 0', borderBottom: '1px solid #0f172a', textDecoration: 'none', color: '#f1f5f9', gap: '2px' },
  hlName:       { fontSize: '13px', color: '#94a3b8' },
  hlVal:        { fontSize: '13px', fontWeight: '700' },

  filterBar:       { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px', alignItems: 'center' },
  filterBtn:       { background: '#1e293b', border: '1px solid #334155', color: '#64748b', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  filterBtnActive: { background: 'rgba(167,139,250,0.15)', border: '1px solid #a78bfa', color: '#a78bfa' },
  searchInput:     { background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', outline: 'none', marginLeft: 'auto' },

  grid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', marginBottom: '48px' },
  card:     { background: '#1e293b', borderRadius: '12px', padding: '16px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '8px' },
  cardTop:  { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' },
  abbr:     { fontSize: '18px', fontWeight: '900', minWidth: '32px' },
  stateName:{ fontSize: '13px', color: '#94a3b8', fontWeight: '600', flex: 1 },
  rankBadge:{ fontSize: '11px', fontWeight: '700' },
  stat:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  statLabel:{ fontSize: '11px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: '600' },
  statVal:  { fontSize: '13px', fontWeight: '800' },
  statValGray:{ fontSize: '13px', fontWeight: '700', color: '#64748b' },

  ctaSection:      { background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.15)', borderRadius: '14px', padding: '24px', textAlign: 'center', marginBottom: '32px' },
  ctaTitle:        { fontSize: '17px', fontWeight: '700', color: '#f1f5f9', marginBottom: '16px' },
  ctaRow:          { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  ctaBtn:          { background: '#a78bfa', color: '#0f172a', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '800', fontSize: '14px' },
  ctaBtnSecondary: { background: '#1e293b', color: '#f1f5f9', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', fontSize: '14px', border: '1px solid #334155' },
};
